/**
 * Hub vidéos — tutos & campagnes officielles
 *
 * Route : /videos
 *
 * Bibliothèque vidéo complète :
 *  - Hero : campagne en cours (CURRENT_CAMPAIGN, 2026 motards expérimentés)
 *  - Vidéos officielles groupées par catégorie (équipement, prévention, débridage…)
 *  - Playlists officielles thématiques
 *  - Chaînes officielles à suivre
 *
 * Toutes les vidéos sont intégrées via youtube-nocookie (CGU YouTube respectées).
 * Source : Délégation à la Sécurité routière + Fondation VINCI + Prévention Routière.
 */
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Play,
  Filter,
  ExternalLink,
  Youtube,
  Tv,
  Sparkles,
  ChevronRight,
  CheckCircle2,
  Eye,
  RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  OFFICIAL_CHANNELS,
  OFFICIAL_PLAYLISTS,
  OFFICIAL_VIDEOS,
  CURRENT_CAMPAIGN,
} from '../data/officialVideos';
import useSeo from '../hooks/useSeo';

const CATEGORIES = [
  { slug: 'all', label: 'Toutes', color: '#cf2031' },
  { slug: 'campagne-2026', label: 'Campagne 2026', color: '#cf2031' },
  { slug: 'trajectoire', label: 'Trajectoire & virages', color: '#16a34a' },
  { slug: 'plateau', label: 'Plateau & maniabilité', color: '#2563eb' },
  { slug: 'equipement', label: 'Équipement & EPI', color: '#ea580c' },
  { slug: 'prevention', label: 'Prévention', color: '#7c3aed' },
  { slug: 'debridage', label: 'Débridage', color: '#9333ea' },
  { slug: 'visibilite', label: 'Visibilité', color: '#0891b2' },
];

function VideoCard({ video, featured, watched, onMarkWatched }) {
  const cat = CATEGORIES.find((c) => c.slug === video.category) || CATEGORIES[0];
  return (
    <div
      className={`group relative rounded-xl overflow-hidden border bg-card transition-all hover:shadow-lg hover:-translate-y-0.5 ${
        featured ? 'lg:col-span-2 lg:row-span-2' : ''
      } ${watched ? 'border-[#16a34a]/60' : 'border-border'}`}
      data-testid={`video-card-${video.videoId}`}
    >
      <a
        href={`https://www.youtube.com/watch?v=${video.videoId}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onMarkWatched?.(video.videoId)}
        className="block"
      >
        <div className="relative bg-black aspect-video overflow-hidden">
          <img
            src={`https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`}
            alt={video.title}
            loading="lazy"
            className={`w-full h-full object-cover transition-transform group-hover:scale-105 ${watched ? 'opacity-70' : ''}`}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/15 transition-colors">
            <div
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-white shadow-lg"
              style={{ background: cat.color }}
            >
              <Play className="w-5 h-5 sm:w-6 sm:h-6 ml-0.5" fill="currentColor" />
            </div>
          </div>
          {video.duration && (
            <span className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
              {video.duration}
            </span>
          )}
          {watched && (
            <span
              className="absolute top-2 right-2 inline-flex items-center gap-1 bg-[#16a34a] text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full shadow"
              data-testid={`watched-badge-${video.videoId}`}
            >
              <CheckCircle2 className="w-3 h-3" /> Vue
            </span>
          )}
        {video.isCampaign && (
          <span
            className="absolute top-2 left-2 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full text-white"
            style={{ background: '#cf2031' }}
          >
            ✨ Campagne 2026
          </span>
        )}
      </div>
      <div className="p-3 sm:p-4">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Badge
            className="text-[9px] font-bold border-0"
            style={{ background: `${cat.color}1a`, color: cat.color }}
          >
            {cat.label}
          </Badge>
          <span className="text-[10px] text-muted-foreground truncate">
            · {video.channel}
          </span>
        </div>
        <h3 className="text-sm sm:text-base font-black leading-snug mb-1.5 group-hover:text-[#cf2031] transition-colors">
          {video.title}
        </h3>
        {video.description && (
          <p className="text-xs text-muted-foreground line-clamp-2 leading-snug">
            {video.description}
          </p>
        )}
        {/* Bouton « Marquer vue » manuel (en cas d'ouverture indirecte) */}
        {!watched && onMarkWatched && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMarkWatched(video.videoId); }}
            className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground hover:text-[#16a34a] transition-colors"
            data-testid={`mark-watched-${video.videoId}`}
          >
            <Eye className="w-3 h-3" /> Marquer comme vue
          </button>
        )}
      </div>
      </a>
    </div>
  );
}

function PlaylistCard({ playlist }) {
  return (
    <a
      href={`https://www.youtube.com/playlist?list=${playlist.listId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group block rounded-xl overflow-hidden border-2 border-dashed border-border bg-card p-4 sm:p-5 transition-all hover:border-[#cf2031]/50 hover:-translate-y-0.5"
      data-testid={`playlist-card-${playlist.id}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-[#cf2031]/15 text-[#cf2031] flex items-center justify-center flex-shrink-0">
          <Tv className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#cf2031]">
            {playlist.kicker || 'Playlist YouTube'}
          </div>
          <h3 className="text-sm sm:text-base font-black leading-tight group-hover:text-[#cf2031] transition-colors">
            {playlist.title}
          </h3>
        </div>
        <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      </div>
      <p className="text-xs text-muted-foreground leading-snug">
        {playlist.description}
      </p>
      <div className="text-[10px] text-muted-foreground mt-2 italic">
        Chaîne : {playlist.channel}
      </div>
    </a>
  );
}

const WATCHED_KEY = 'c2r:videos:watched:v1';

/**
 * Hook localStorage : suivi des vidéos vues par l'utilisateur.
 * Retourne { watchedSet, markWatched, resetAll, isWatched, count }
 */
function useWatchedVideos() {
  const [watched, setWatched] = useState(() => {
    try {
      const raw = localStorage.getItem(WATCHED_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch (_e) {
      return new Set();
    }
  });

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(WATCHED_KEY, JSON.stringify(Array.from(next)));
    } catch (_e) { /* quota */ }
  }, []);

  const markWatched = useCallback((id) => {
    setWatched((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      persist(next);
      return next;
    });
  }, [persist]);

  const resetAll = useCallback(() => {
    setWatched(new Set());
    persist(new Set());
  }, [persist]);

  const isWatched = useCallback((id) => watched.has(id), [watched]);
  return { watched, markWatched, resetAll, isWatched };
}

export default function Videos() {
  useSeo({
    title: 'Vidéos & tutos moto · Campagnes officielles Sécurité routière',
    description:
      "Toutes les vidéos officielles Sécurité routière, Fondation VINCI Autoroutes, Prévention Routière : campagnes 2026, équipement EPI, débridage, visibilité, prévention.",
    path: '/videos',
  });

  const [activeCat, setActiveCat] = useState('all');
  const { watched, markWatched, resetAll, isWatched } = useWatchedVideos();

  const filteredVideos = useMemo(() => {
    if (activeCat === 'all') return OFFICIAL_VIDEOS;
    return OFFICIAL_VIDEOS.filter((v) => v.category === activeCat);
  }, [activeCat]);

  const counts = useMemo(() => {
    const c = { all: OFFICIAL_VIDEOS.length };
    CATEGORIES.forEach((cat) => {
      if (cat.slug !== 'all') c[cat.slug] = OFFICIAL_VIDEOS.filter((v) => v.category === cat.slug).length;
    });
    return c;
  }, []);

  // Sous-comptage des vidéos vues par catégorie (re-calculé quand `watched` change)
  const watchedCounts = useMemo(() => {
    const wc = { all: 0 };
    CATEGORIES.forEach((cat) => { if (cat.slug !== 'all') wc[cat.slug] = 0; });
    OFFICIAL_VIDEOS.forEach((v) => {
      if (watched.has(v.videoId)) {
        wc.all = (wc.all || 0) + 1;
        if (wc[v.category] !== undefined) wc[v.category] += 1;
      }
    });
    return wc;
  }, [watched]);

  const totalWatched = watchedCounts.all || 0;
  const totalVideos = OFFICIAL_VIDEOS.length;
  const progressPct = totalVideos > 0 ? Math.round((totalWatched / totalVideos) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="videos-page">
      {/* HERO Campagne */}
      <section
        className="mc-card relative overflow-hidden"
        style={{ borderLeft: '6px solid #cf2031' }}
        data-testid="hero-campagne"
      >
        <div
          className="absolute -top-16 -right-16 w-72 h-72 rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: '#cf2031' }}
          aria-hidden="true"
        />
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-0">
          <div className="p-5 sm:p-7 lg:p-9">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#cf2031]/15 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[#cf2031] mb-3">
              <Sparkles className="w-3 h-3" /> {CURRENT_CAMPAIGN.kicker}
            </div>
            <h1
              className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.05]"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              data-testid="videos-title"
            >
              {CURRENT_CAMPAIGN.title}
            </h1>
            <p className="text-sm sm:text-base text-foreground/80 mt-3 leading-relaxed">
              {CURRENT_CAMPAIGN.subtitle}
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button asChild className="gap-1.5" style={{ background: '#cf2031' }}>
                <a
                  href={`https://www.youtube.com/watch?v=${CURRENT_CAMPAIGN.heroVideoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-play-cta"
                >
                  <Play className="w-4 h-4" fill="currentColor" /> Regarder ({CURRENT_CAMPAIGN.heroVideoDuration})
                </a>
              </Button>
              <Button asChild variant="outline" className="gap-1.5">
                <Link to="/campagne-2026-motards-experimentes">
                  Lire l&apos;analyse <ChevronRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
            <div className="text-[10px] italic text-muted-foreground mt-3">
              Réalisation : {CURRENT_CAMPAIGN.directors} · {CURRENT_CAMPAIGN.channel}
            </div>
          </div>
          <a
            href={`https://www.youtube.com/watch?v=${CURRENT_CAMPAIGN.heroVideoId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="relative aspect-video lg:aspect-auto bg-black overflow-hidden group"
          >
            <img
              src={`https://i.ytimg.com/vi/${CURRENT_CAMPAIGN.heroVideoId}/maxresdefault.jpg`}
              alt={CURRENT_CAMPAIGN.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/15 transition-colors">
              <div
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-white shadow-xl"
                style={{ background: '#cf2031' }}
              >
                <Play className="w-7 h-7 sm:w-8 sm:h-8 ml-1" fill="currentColor" />
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* INTRO */}
      <header className="px-1">
        <h2 className="text-xl sm:text-2xl font-black tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Toutes les vidéos officielles
        </h2>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl">
          Campagnes nationales Sécurité routière, prévention, équipement, débridage, visibilité.
          Pour s&apos;informer, motiver et réfléchir.
        </p>
      </header>

      {/* PROGRESSION DE VISIONNAGE */}
      <section
        className="mc-card p-3 sm:p-4 flex items-center gap-3 flex-wrap"
        data-testid="videos-progress"
      >
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0"
          style={{ background: '#16a34a' }}>
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-black uppercase tracking-wider text-[#16a34a]">
            Ma progression vidéos
          </div>
          <div className="text-sm font-bold mt-0.5">
            <span data-testid="watched-count">{totalWatched}</span> / {totalVideos} tutos vues
            <span className="text-xs font-bold text-muted-foreground ml-2">({progressPct}%)</span>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${progressPct}%`, background: '#16a34a' }}
            />
          </div>
        </div>
        {totalWatched > 0 && (
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Réinitialiser ta progression vidéos ?')) resetAll();
            }}
            className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground hover:text-foreground"
            data-testid="reset-watched"
          >
            <RotateCcw className="w-3 h-3" /> Réinitialiser
          </button>
        )}
      </section>

      {/* FILTRES CATÉGORIES */}
      <section className="mc-card p-3 sm:p-4" data-testid="category-filters">
        <div className="flex items-center gap-1.5 mb-2 text-[10px] font-black uppercase tracking-wider text-muted-foreground">
          <Filter className="w-3 h-3" /> Filtrer par catégorie
        </div>
        <div className="flex flex-wrap gap-1.5">
          {CATEGORIES.map((cat) => {
            const active = activeCat === cat.slug;
            const count = counts[cat.slug] ?? 0;
            const wCount = watchedCounts[cat.slug] ?? 0;
            return (
              <button
                key={cat.slug}
                type="button"
                onClick={() => setActiveCat(cat.slug)}
                data-testid={`cat-filter-${cat.slug}`}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all ${
                  active ? 'text-white border-transparent' : 'bg-background border-border hover:border-foreground/40'
                }`}
                style={active ? { background: cat.color } : undefined}
              >
                {cat.label}
                <span className={`text-[10px] font-black ${active ? 'text-white/80' : 'text-muted-foreground'}`}>
                  · {wCount}/{count}
                </span>
                {wCount === count && count > 0 && (
                  <CheckCircle2 className={`w-3 h-3 ${active ? 'text-white' : 'text-[#16a34a]'}`} />
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* GRILLE VIDÉOS */}
      <section data-testid="videos-grid">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {filteredVideos.map((v) => (
            <VideoCard
              key={v.videoId}
              video={v}
              watched={isWatched(v.videoId)}
              onMarkWatched={markWatched}
            />
          ))}
          {filteredVideos.length === 0 && (
            <div className="col-span-full text-center py-10 text-sm text-muted-foreground italic border border-dashed border-border rounded-xl">
              Aucune vidéo dans cette catégorie pour le moment.
            </div>
          )}
        </div>
      </section>

      {/* PLAYLISTS */}
      <section data-testid="playlists-section">
        <h2 className="text-lg sm:text-xl font-black tracking-tight mb-3 flex items-center gap-2">
          <Tv className="w-4 h-4 text-[#cf2031]" /> Playlists officielles
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {OFFICIAL_PLAYLISTS.map((pl) => (
            <PlaylistCard key={pl.id} playlist={pl} />
          ))}
        </div>
      </section>

      {/* CHAÎNES */}
      <section data-testid="channels-section">
        <h2 className="text-lg sm:text-xl font-black tracking-tight mb-3 flex items-center gap-2">
          <Youtube className="w-5 h-5 text-[#cf2031]" /> Chaînes à suivre
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {OFFICIAL_CHANNELS.map((ch) => (
            <a
              key={ch.handle}
              href={ch.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block rounded-xl border border-border bg-card p-4 sm:p-5 transition-all hover:border-[#cf2031]/50 hover:-translate-y-0.5"
              data-testid={`channel-${ch.handle}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Youtube className="w-5 h-5 text-[#cf2031]" />
                <span className="text-[10px] text-muted-foreground font-mono">{ch.handle}</span>
              </div>
              <h3 className="font-black text-sm sm:text-base mb-1 group-hover:text-[#cf2031] transition-colors">
                {ch.name}
              </h3>
              <p className="text-xs text-muted-foreground leading-snug mb-2">
                {ch.description}
              </p>
              <div className="text-[10px] text-muted-foreground tabular-nums">
                {ch.subscribers} abonnés · {ch.videoCount} vidéos
              </div>
            </a>
          ))}
        </div>
      </section>

      <div className="text-[11px] italic text-muted-foreground px-1 pt-2">
        Toutes les vidéos sont la propriété exclusive de leurs auteurs respectifs.
        Intégrations via YouTube-nocookie · CGU YouTube respectées · Aucune ré-hébergement.
      </div>
    </div>
  );
}
